const toFiniteScore = (value) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

export const getBestAssessmentScore = (attempts = []) => {
  let bestScore = null

  for (const attempt of attempts) {
    const nextScore = toFiniteScore(
      attempt && typeof attempt === 'object' && 'score' in attempt
        ? attempt.score
        : attempt
    )

    if (nextScore == null) continue
    bestScore = bestScore == null ? nextScore : Math.max(bestScore, nextScore)
  }

  return bestScore
}

export const isCourseAssessmentApproved = ({ bestScore, minGrade }) => {
  const normalizedBestScore = toFiniteScore(bestScore)
  const normalizedMinGrade = toFiniteScore(minGrade)

  if (normalizedBestScore == null || normalizedMinGrade == null) {
    return false
  }

  return normalizedBestScore >= normalizedMinGrade
}

export const getCourseCompletionEligibility = ({
  progressPercent,
  assessmentEnabled,
  assessmentId,
  bestScore,
  minGrade,
}) => {
  const normalizedProgress = toFiniteScore(progressPercent) ?? 0
  const progressComplete = normalizedProgress >= 100
  const assessmentRequired = Boolean(assessmentEnabled && assessmentId)
  const assessmentApproved = assessmentRequired
    ? isCourseAssessmentApproved({ bestScore, minGrade })
    : false

  return {
    canComplete: progressComplete && (!assessmentRequired || assessmentApproved),
    progressComplete,
    assessmentRequired,
    assessmentApproved,
  }
}

export const getCourseProgressVerdict = ({
  progressPercent,
  assessmentEnabled,
  assessmentId,
  bestScore,
  minGrade,
  attemptsCount,
}) => {
  const eligibility = getCourseCompletionEligibility({
    progressPercent,
    assessmentEnabled,
    assessmentId,
    bestScore,
    minGrade,
  })

  if (!eligibility.progressComplete) {
    return {
      status: 'Em andamento',
      result: '-',
      eligibleForCompletion: false,
    }
  }

  if (!eligibility.assessmentRequired) {
    return {
      status: 'Concluído',
      result: '-',
      eligibleForCompletion: eligibility.canComplete,
    }
  }

  if (eligibility.assessmentApproved) {
    return {
      status: 'Concluído',
      result: 'Aprovado',
      eligibleForCompletion: true,
    }
  }

  return {
    status: 'Concluído',
    result: Number(toFiniteScore(attemptsCount) ?? 0) > 0 ? 'Reprovado' : '-',
    eligibleForCompletion: false,
  }
}

export const isCourseAssessmentRetakeLocked = ({
  completedCount,
  maxAttempts,
  minGrade,
  bestScore,
}) => {
  if (isCourseAssessmentApproved({ bestScore, minGrade })) {
    return true
  }

  const normalizedMaxAttempts = toFiniteScore(maxAttempts)
  if (normalizedMaxAttempts == null || normalizedMaxAttempts <= 0) {
    return false
  }

  const normalizedCompletedCount = toFiniteScore(completedCount) ?? 0
  return normalizedCompletedCount >= normalizedMaxAttempts
}

export const shouldSyncCertificateFinalGrade = ({
  currentFinalGrade,
  bestScore,
}) => {
  const normalizedBestScore = toFiniteScore(bestScore)
  if (normalizedBestScore == null) {
    return false
  }

  const normalizedCurrentGrade = toFiniteScore(currentFinalGrade)
  if (normalizedCurrentGrade == null) {
    return true
  }

  return normalizedCurrentGrade < normalizedBestScore
}
